/**
 * useInventories Hook
 *
 * Fetches inventories from the Rails API with support for
 * status filtering, search, sorting, and pagination.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { SUCCESS_TOAST_DURATION } from '@/react/constants';
import { useTranslation } from './useTranslation';
import { inventoriesQueryKeys, getInventories, getInventory } from '../api/inventories';
import { createInventory, updateInventory } from '../api/inventories';
import type { InventoriesResponse, InventoryShowResponse, UpdateInventoryPayload, CreateInventoryPayload, FetchInventoriesParams } from '../api/inventories';

const STALE_TIME = 30_000;

// Re-export for consumers that import query keys from hooks
export const inventoryQueryKeys = inventoriesQueryKeys;

interface UseInventoriesOptions {
  params?: FetchInventoriesParams;
  initialData?: InventoriesResponse;
}

export function useInventories({ params = {}, initialData }: UseInventoriesOptions = {}) {
  return useQuery({
    queryKey: inventoriesQueryKeys.list(params),
    queryFn: () => getInventories(params),
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
    ...(initialData ? { initialData, initialDataUpdatedAt: Date.now() } : {}),
  });
}

interface UseInventoryOptions {
  initialData?: InventoryShowResponse;
}

export function useInventory(id: number, { initialData }: UseInventoryOptions = {}) {
  return useQuery({
    queryKey: inventoriesQueryKeys.detail(id),
    queryFn: () => getInventory(id),
    staleTime: STALE_TIME,
    ...(initialData ? { initialData, initialDataUpdatedAt: Date.now() } : {}),
  });
}

interface UseUpdateInventoryOptions {
  onSuccess?: (data: InventoryShowResponse) => void;
}

export function useUpdateInventory(options?: UseUpdateInventoryOptions) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: UpdateInventoryPayload }) => {
      return updateInventory(id, payload);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: inventoriesQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: inventoriesQueryKeys.detail(variables.id) });

      toast({ description: t('toast.updated'), duration: SUCCESS_TOAST_DURATION });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      toast({
        description: error.message || t('toast.update_error'),
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateInventoryStatus() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({ id, active }: { id: number; active: boolean }) => {
      return updateInventory(id, { active });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: inventoriesQueryKeys.lists(), refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: inventoriesQueryKeys.detail(variables.id) });

      toast({ description: t('toast.status_updated'), duration: SUCCESS_TOAST_DURATION });
    },
    onError: (error) => {
      toast({
        description: error.message || t('toast.status_update_error'),
        variant: 'destructive',
      });
    },
  });
}

interface UseCreateInventoryOptions {
  onSuccess?: (inventory: InventoryShowResponse['inventory']) => void;
}

export function useCreateInventory(options?: UseCreateInventoryOptions) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (payload: CreateInventoryPayload) => {
      return createInventory(payload);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: inventoriesQueryKeys.lists() });
      options?.onSuccess?.(data.inventory);
    },
    onError: (error) => {
      toast({
        description: error.message || t('toast.create_error'),
        variant: 'destructive',
      });
    },
  });
}
